<template>
  <div>
    <h1 class="mdn-Heading mdn-Heading--sm">Gestão de E-mails Automáticos</h1>

    <div class="d-flex align-items-start justify-content-between mb-2">
      <p class="text-muted mb-0">
        Configure os e-mails automáticos enviados pelo sistema: destinatários,
        assunto e status conforme o evento ocorrido.
      </p>
      <button
        type="button"
        class="v-btn v-btn--has-bg v-btn--rounded theme--light v-size--default primary text-capitalize ml-4 flex-shrink-0"
        @click="$router.push({ name: 'gestao-emails-automaticos-criar' })"
      >
        <SGPIconComponent icon="mdi-plus" class="mr-1" />
        Novo Template
      </button>
    </div>

    <div class="row align-items-end mb-3">
      <div class="col-md-4">
        <SGPFormInputComponent
          type="text"
          name="filtro_event_key"
          v-model="filtroEventKey"
          placeholder="Filtrar por evento"
          @input="filtrarComDebounce"
        />
      </div>
      <div class="col-md-3">
        <SGPFormSelectComponent
          name="filtro_status"
          v-model="filtroStatus"
          :listaDeOptions="opcoesStatus"
          optionLabel="label"
          optionValue="value"
          placeholder="Status"
          @change="aplicarFiltros"
        />
      </div>
    </div>

    <SGPPaginacaoComponent
      v-if="listaDeTemplates.length"
      :paginacao="paginacao"
      :key="paginacao && paginacao.current_page"
      @sgpAtualizarPaginacao="realizarPaginacao"
    >
      <SGPTabelaAvancadaComponent
        :colunas="colunas"
        :dados="dadosTabela"
        :key="JSON.stringify(dadosTabela)"
        :usuarioPodeEditar="false"
        :usuarioPodeExcluir="false"
        selecionarColunas
        :filtroText="true"
      >
        <template v-slot:item.destinatarios_resumo="{ item }">
          <span class="dest-resumo-celula" :title="item.destinatarios_completo">
            {{ item.destinatarios_resumo }}
          </span>
        </template>

        <template v-slot:item.acoes="{ item }">
          <button
            class="v-btn v-btn--text v-btn--icon theme--light v-size--small"
            title="Abrir configuração"
            @click="abrirConfiguracao(item)"
          >
            <SGPIconComponent icon="mdi-pencil" />
          </button>
          <button
            class="v-btn v-btn--text v-btn--icon theme--light v-size--small"
            :title="item.is_active ? 'Desativar envio' : 'Ativar envio'"
            @click="alternarStatus(item)"
          >
            <SGPIconComponent :icon="item.is_active ? 'mdi-bell' : 'mdi-bell-off'" />
          </button>
        </template>
      </SGPTabelaAvancadaComponent>
    </SGPPaginacaoComponent>

    <SGPAlertaComponent v-else />

    <SGPLoadingComponent v-if="loadingTemplates || salvandoTemplate" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { resumirDestinatarios, formatarDestinatario } from '../helpers/destinatarios'

export default {
  name: 'GestaoEmailsAutomaticosPage',

  data () {
    return {
      filtroEventKey: '',
      filtroStatus: '',
      debounceFiltro: null
    }
  },

  created () {
    this.listarTemplates()
  },

  computed: {
    ...mapGetters('gestaoEmailsAutomaticos', [
      'listaDeTemplates',
      'loadingTemplates',
      'salvandoTemplate',
      'paginacao'
    ]),

    opcoesStatus () {
      return [
        { value: '', label: 'Todos' },
        { value: true, label: 'Ativo' },
        { value: false, label: 'Inativo' }
      ]
    },

    colunas () {
      return [
        { key: 'acoes', label: 'Ações', sortable: false, width: '120' },
        { key: 'name', label: 'E-mail', sortable: true, width: '220' },
        { key: 'event_key', label: 'Evento que dispara', sortable: true, width: '220' },
        { key: 'destinatarios_resumo', label: 'Destinatários', sortable: false, width: '260' },
        { key: 'status_label', label: 'Status', sortable: false, width: '100' },
        { key: 'ultima_alteracao', label: 'Última Alteração', sortable: true, width: '160' }
      ]
    },

    dadosTabela () {
      return (this.listaDeTemplates || []).map(item => ({
        ...item,
        destinatarios_resumo: this.resumirParaTabela(item.recipients),
        destinatarios_completo: resumirDestinatarios(item.recipients),
        status_label: item.is_active ? 'Ativo' : 'Inativo',
        ultima_alteracao: this.formatarData(item.updated_at)
      }))
    }
  },

  methods: {
    ...mapActions('gestaoEmailsAutomaticos', [
      'listarTemplates',
      'atualizarFiltros',
      'alternarStatusTemplate'
    ]),

    resumirParaTabela (recipients) {
      if (!Array.isArray(recipients) || !recipients.length) return '-'
      const MAX = 2
      const nomes = recipients.map(r => formatarDestinatario(r))
      if (nomes.length <= MAX) return nomes.join(', ')
      return `${nomes.slice(0, MAX).join(', ')} e mais ${nomes.length - MAX}`
    },

    abrirConfiguracao (item) {
      this.$router.push({
        name: 'gestao-emails-automaticos-editar',
        params: { id: String(item.id) }
      })
    },

    aplicarFiltros () {
      this.atualizarFiltros({
        event_key: this.filtroEventKey || '',
        is_active: this.filtroStatus === '' ? null : this.filtroStatus
      })
      this.listarTemplates({ page: 1, current_page: 1 })
    },

    filtrarComDebounce () {
      if (this.debounceFiltro) clearTimeout(this.debounceFiltro)
      this.debounceFiltro = setTimeout(() => this.aplicarFiltros(), 400)
    },

    realizarPaginacao (paginate) {
      const current = paginate.current_page || 1
      const perPage = paginate.per_page || this.paginacao?.per_page || 15
      this.atualizarFiltros({})
      this.listarTemplates({ page: current, current_page: current, per_page: perPage })
    },

    alternarStatus (item) {
      this.alternarStatusTemplate(item)
    },

    formatarData (data) {
      if (!data) return '-'
      const d = new Date(data)
      if (isNaN(d.getTime())) return '-'
      return d.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.dest-resumo-celula {
  display: block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
